import { useState, useRef, useEffect, useCallback } from 'react';
import update from 'immutability-helper';

const NotesReminderMax = 10000; // ms — matches the original constant

const initialBookmarks = {
  isBookmarkFldEditorOpen: false,
  bookmarkFldID: '',
  bookmarkData: [],
  currBookmarkDesc: '',
  currBookmarkTimestamp: '',
};

/**
 * useBookmarks
 *
 * Owns the bookmark / preparer-notes state and related methods for FormViewer.
 *
 * State owned:
 *   bookmarks           — { isBookmarkFldEditorOpen, bookmarkFldID, bookmarkData, currBookmarkDesc, currBookmarkTimestamp }
 *   bookmarkField       — active field for the form-nav jump-to-bookmark logic
 *                          (also synced into bookmarkFieldRef so useFormNavigation can
 *                          read it synchronously in jQuery handlers)
 *   notes               — array of preparer notes
 *   hasNotes            — true when the return has notes (shown in header)
 *   isNotesAlertOpen    — transient alert state (NotesReminderMax duration)
 *
 * Methods exported:
 *   toggleFromBookmark, handleBookmarkField, setBookmarkFldEditor,
 *   deleteBookmark, setBookmarkByFldID, handleNoteUpsert,
 *   handlePreparerNotesReq, doNotesAlert, toggleNotesForm
 *   + setters: setHasNotes, setBookmarkFldID
 *
 * Inputs:
 *   calcAPIRef       — ref to CalcAPI instance (from useCalcSession)
 *   bookmarkFieldRef — shared ref; this hook keeps .current in sync with bookmarkField state
 *   formNavRef       — synchronously-current ref to formNav result (for activeForm / getTaxForm)
 */
const useBookmarks = ({ calcAPIRef, bookmarkFieldRef, formNavRef }) => {
  // ── State ──────────────────────────────────────────────────────────────────
  const [bookmarks, setBookmarksState] = useState(initialBookmarks);
  const [bookmarkField, setBookmarkFieldState] = useState('');
  const [notes, setNotes] = useState([]);
  const [hasNotes, setHasNotes] = useState(false);
  const [isNotesAlertOpen, setIsNotesAlertOpen] = useState(false);

  // ── Sync bookmarkField → bookmarkFieldRef ──────────────────────────────────
  const setBookmarkField = useCallback(
    val => {
      setBookmarkFieldState(val);
      if (bookmarkFieldRef) bookmarkFieldRef.current = val;
    },
    [bookmarkFieldRef],
  );

  // ── Stable refs to avoid stale closures in callbacks ─────────────────────
  const bookmarksRef = useRef(bookmarks);
  useEffect(() => {
    bookmarksRef.current = bookmarks;
  }, [bookmarks]);

  // ── toggleFromBookmark ────────────────────────────────────────────────────
  const toggleFromBookmark = useCallback(
    (_toggle, field) => {
      setBookmarkField(field);
    },
    [setBookmarkField],
  );

  // ── handleBookmarkField ───────────────────────────────────────────────────
  const handleBookmarkField = useCallback((bookmarkData, isFieldEditor = true) => {
    let newBookmark = {};
    if (!isFieldEditor) {
      newBookmark = update(bookmarksRef.current, {
        bookmarkData: { $set: bookmarkData },
      });
    } else {
      newBookmark = update(bookmarksRef.current, {
        isBookmarkFldEditorOpen: { $set: isFieldEditor },
        currBookmarkDesc: { $set: bookmarkData[0].desc },
        currBookmarkTimestamp: { $set: bookmarkData[0].timestamp },
      });
    }
    setBookmarksState(newBookmark);
  }, []);

  // ── setBookmarkFldEditor ──────────────────────────────────────────────────
  const setBookmarkFldEditor = useCallback(val => {
    setBookmarksState(prev => ({
      ...prev,
      isBookmarkFldEditorOpen: val,
      currBookmarkDesc: '',
      currBookmarkTimestamp: '',
    }));
  }, []);

  // ── setBookmarkFldID ──────────────────────────────────────────────────────
  // Called by fieldEventsCallbacksRef.onSetBookmarkFldID
  const setBookmarkFldID = useCallback(id => {
    setBookmarksState(prev => ({ ...prev, bookmarkFldID: id }));
  }, []);

  // ── deleteBookmark ────────────────────────────────────────────────────────
  const deleteBookmark = useCallback(fldID => {
    setBookmarksState(prev => ({
      ...prev,
      bookmarkData: prev.bookmarkData.filter(b => b.field !== fldID),
      currBookmarkDesc: '',
      currBookmarkTimestamp: '',
    }));
  }, []);

  // ── setBookmarkByFldID ────────────────────────────────────────────────────
  const setBookmarkByFldID = useCallback(
    (desc, timestamp, shouldDeleteBookmark = false) => {
      calcAPIRef.current.ReturnBookmarkUpsert(
        formNavRef.current?.activeForm ?? '',
        bookmarksRef.current.bookmarkFldID,
        desc,
        timestamp,
        shouldDeleteBookmark,
      );
      setBookmarksState(prev => ({
        ...prev,
        currBookmarkDesc: '',
        currBookmarkTimestamp: '',
      }));
      setBookmarkFldEditor(false);
    },
    [calcAPIRef, formNavRef, setBookmarkFldEditor],
  );

  // ── handleNoteUpsert ──────────────────────────────────────────────────────
  const handleNoteUpsert = useCallback(
    (note, shouldDelete = false) => {
      calcAPIRef.current.ReturnNotesUpsert(note, shouldDelete);
    },
    [calcAPIRef],
  );

  // ── handlePreparerNotesReq ────────────────────────────────────────────────
  // Called when the calc server returns a notes array.
  const handlePreparerNotesReq = useCallback(newNotes => {
    const sorted = [...newNotes].sort((a, b) => a.id - b.id);
    setNotes(sorted);
  }, []);

  // ── doNotesAlert ──────────────────────────────────────────────────────────
  const doNotesAlert = useCallback(() => {
    setIsNotesAlertOpen(true);
    setTimeout(() => {
      setIsNotesAlertOpen(false);
      setHasNotes(false);
    }, NotesReminderMax);
  }, []);

  // ── toggleNotesForm ───────────────────────────────────────────────────────
  const toggleNotesForm = useCallback(() => {
    formNavRef.current?.navigateToNotesForm?.();
  }, [formNavRef]);

  // ── Return ─────────────────────────────────────────────────────────────────
  return {
    // State for rendering / external reads
    bookmarks,
    bookmarkField,
    notes,
    hasNotes,
    isNotesAlertOpen,

    // Setters needed by FormViewer class
    setBookmarkField,
    setBookmarkFldID,
    setHasNotes,
    setIsNotesAlertOpen,

    // Methods registered in callbacks or passed to components
    toggleFromBookmark,
    handleBookmarkField,
    setBookmarkFldEditor,
    deleteBookmark,
    setBookmarkByFldID,
    handleNoteUpsert,
    handlePreparerNotesReq,
    doNotesAlert,
    toggleNotesForm,
  };
};

export default useBookmarks;
